"use client";

import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useParams } from "next/navigation";
import Logo from "@/components/customs/Logo";
import { getSessionApiKeySync } from "@/lib/cookies";
import SelectLanguage from "./SelectLanguage";
import ProtectedContent from "./ProtectedContent";
import VersionLogger from "./VersionLogger";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import SheetMainMenu from "./SheetMainMenu";
import { type SupportedLanguage } from "@/lib/shared";
import { resolveLocale } from "@/lib/resolvedLocale";
import { localizedPath } from "@/lib/localePath";
import lang from "@/app/[locale]/(main)/lang.json";
import SessionSection from "./SessionSection";
import { AUTH_SESSION_CHANGED } from "@/lib/authSession";

export default function MainHeader() {
  const params = useParams();
  const location = useLocation();
  const [session, setSession] = useState<string | undefined>(() =>
    typeof document !== "undefined" ? getSessionApiKeySync() : undefined,
  );

  const routeLocale = useMemo(
    (): SupportedLanguage => resolveLocale(params.locale as string | undefined),
    [params.locale],
  );

  useLayoutEffect(() => {
    setSession(getSessionApiKeySync());
  }, [location.pathname, location.key]);

  useEffect(() => {
    const onSession = () => {
      setSession(getSessionApiKeySync());
    };
    window.addEventListener(AUTH_SESSION_CHANGED, onSession);
    return () => window.removeEventListener(AUTH_SESSION_CHANGED, onSession);
  }, []);

  return (
    <header
      id="main-header"
      className="py-2.5 lg:py-6 border-b border-gray-200 bg-white px-4 xl:px-0"
    >
      <div className="relative flex w-full items-center gap-x-4 px-0 lg:px-4">
        <div
          className={cn(
            "flex min-w-[2.75rem] flex-1 basis-0 items-center justify-start",
            "lg:min-w-0 lg:flex-none lg:basis-auto",
          )}
        >
          <SessionSection>
            <span className="flex flex-none md:hidden">
              <SheetMainMenu locale={routeLocale} />
            </span>
          </SessionSection>
        </div>

        <Logo
          wrapperClassName={cn(
            "flex-1 sm:flex-none rtl:ml-auto md:rtl:ml-0",
            !session &&
              "max-lg:absolute max-lg:left-1/2 max-lg:top-1/2 max-lg:z-[5] max-lg:flex-none max-lg:-translate-x-1/2 max-lg:-translate-y-1/2 max-lg:shrink-0",
          )}
        />

        <div
          className={cn(
            "flex min-w-[2.75rem] flex-1 basis-0 items-center justify-end gap-x-4 lg:gap-x-8 lg:ms-auto",
            "lg:min-w-0 lg:flex-none lg:basis-auto",
          )}
        >
          <SessionSection>
            {session ? (
              <ProtectedContent />
            ) : (
              <Link
                to={localizedPath(routeLocale, "/auth/login")}
                className={buttonVariants({
                  variant: "default",
                  size: "sm",
                  rounded: "full",
                  className: cn(
                    "h-12 min-h-12 px-6 py-3 text-sm",
                  ),
                })}
              >
                {lang["login"][routeLocale]}
              </Link>
            )}
          </SessionSection>

          <span className="hidden md:inline-flex">
            <SelectLanguage />
          </span>
        </div>
      </div>
      <VersionLogger />
    </header>
  );
}
